import React, { useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Alert, Box, Button, Card, CardContent, Divider, Stack, TextField, Typography,
} from '@mui/material';
import { PlaceOutlined as AreaIcon } from '@mui/icons-material';
import HiringService, { MessageTemplate, InterviewGuide } from '../../api/services/hiring.service';
import MessageTemplates from './MessageTemplates';
import InterviewGuideEditor from './InterviewGuideEditor';
import CompanyDetails from './CompanyDetails';

import apiClient from '../../api/client';
import { useAuthStore } from '../../store/authStore';
import type { User } from '../../types';
import PageHeader from '../../components/ui/PageHeader';
import PageBody, { READING_WIDTH } from '../../components/ui/PageBody';
import { EmptyState, LoadingState } from '../../components/ui/States';
import ToneChip from '../../components/ui/ToneChip';
import SettingsPanel from './SettingsPanel';
import { useScrollSpy, scrollToSection } from '../../components/ui/useScrollSpy';

interface ServiceArea {
  serviceAreaId: number;
  csa: string;
  businessName: string;
  stationId: string;
  stationName: string;
  isActive: boolean;
}

// The panel's anchors, in the order they appear on the page.
/** The numbers the interview quotes, which are all that differ per company. */
const PAY_FIELDS: Array<{ field: string; label: string; hint: string; step?: string }> = [
  { field: 'defaultDayRate', label: 'Day rate', hint: 'Base pay per day, in dollars' },
  { field: 'stopThreshold', label: 'Stop threshold', hint: 'Stops a day before the bonus starts' },
  { field: 'overStopRate', label: 'Over threshold', hint: 'Paid per stop above it', step: '0.01' },
  { field: 'trainingDayRate', label: 'Training pay', hint: 'Per day while training' },
];

const SETTINGS_SECTIONS = ['profile', 'company', 'service-areas', 'hiring'];

const Settings: React.FC = () => {
  const { user: authUser, updateUser } = useAuthStore();
  
  const [serviceAreas, setServiceAreas] = useState<ServiceArea[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeServiceAreaId, setActiveServiceAreaId] = useState<number | null>(null);
  const [userInfo, setUserInfo] = useState<Partial<User> | null>(null);
  const [editingProfile, setEditingProfile] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  /**
   * The day rate a new driver is offered. It lives here rather than on
   * every candidate because it is the same number every time — the
   * outreach message quotes it, and being asked for it per candidate was
   * the complaint. A rate agreed with a particular person still overrides
   * it, in their own Details.
   */
  /**
   * The four numbers the messages and the interview script quote. They
   * are company settings rather than script text because the words are
   * identical between companies and only these differ — a script copied
   * per company is how somebody gets quoted the other company's rate.
   */
  const [pay, setPay] = useState<Record<string, string>>({
    defaultDayRate: '', stopThreshold: '', overStopRate: '', trainingDayRate: '',
  });
  const [paySaved, setPaySaved] = useState(false);
  /** Days of silence before a candidacy closes itself; '0' turns it off. */
  const [autoClose, setAutoClose] = useState('');
  /** The wording of the six candidate messages, as this company has it. */
  const [templates, setTemplates] = useState<MessageTemplate[]>([]);
  const [placeholders, setPlaceholders] = useState<string[]>([]);
  const [guide, setGuide] = useState<InterviewGuide | null>(null);
  

  useEffect(() => {
    HiringService.settings()
      .then((settings) => {
        setPay({
          defaultDayRate: settings.defaultDayRate === null ? '' : String(settings.defaultDayRate),
          stopThreshold: settings.stopThreshold === null ? '' : String(settings.stopThreshold),
          overStopRate: settings.overStopRate === null ? '' : String(settings.overStopRate),
          trainingDayRate: settings.trainingDayRate === null ? '' : String(settings.trainingDayRate),
        });
        setAutoClose(settings.autoCloseDays === null || settings.autoCloseDays === undefined
          ? '' : String(settings.autoCloseDays));
        setTemplates(settings.templates || []);
        setPlaceholders(settings.placeholders || []);
        setGuide(settings.interviewGuide || null);
      })
      .catch(() => { /* Hiring is not every company's feature; stay quiet. */ });
  }, []);

  const savePay = useCallback(async (field: string, value: string) => {
    try {
      await HiringService.saveSettings({ [field]: value === '' ? null : Number(value) } as any);
      setPaySaved(true);
    } catch {
      setError('That number could not be saved.');
    }
  }, []);

  const fetchServiceAreas = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await apiClient.get<any, any>('/api/data/service-areas');
      const areas = Array.isArray(response) ? response : (response?.serviceAreas || []);
      setServiceAreas(areas);
      setActiveServiceAreaId(areas.find((area: ServiceArea) => area.isActive)?.serviceAreaId || null);
    } catch (err: any) {
      console.error('Failed to fetch service areas:', err);
      setError('Failed to load service areas');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchUserInfo = useCallback(async () => {
    try {
      // Use user from authStore if available
      if (authUser) {
        setUserInfo(authUser);
        return;
      }
      
      // Fallback: fetch from API
      const response = await apiClient.get<any, any>('/api/user/me');
      
      let user;
      if (response?.user) {
        user = response.user;
      } else if (response?.data?.user) {
        user = response.data.user;
      } else {
        user = response;
      }
      
      setUserInfo(user);
    } catch (err: any) {
      console.error('Failed to fetch user info:', err);
    }
  }, [authUser]);

  const handleUserInfoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setUserInfo((prev: Partial<User> | null) => (prev ? { ...prev, [name]: value } : null));
  };

  const handleUserUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setError('');
      setSuccess('');
      const userUpdate = {
        firstName: userInfo?.firstName,
        lastName: userInfo?.lastName,
        email: userInfo?.email,
        phone: userInfo?.phone,
        profilePhoto: userInfo?.profilePhoto,
      };
      await apiClient.put('/api/user/me', userUpdate);
      // Into the store, not just this screen. fetchUserInfo() reads the
      // store, so without this the form reverted to the old values the
      // moment it stopped editing.
      updateUser(userUpdate);
      setSuccess('Profile updated successfully!');
      setEditingProfile(false);
    } catch (err: any) {
      console.error('Failed to update user info:', err);
      setError('Failed to update profile');
    }
  };

  const handleSetActive = async (serviceAreaId: number) => {
    try {
      setError('');
      await apiClient.post('/api/data/set-active-service-area', { serviceAreaId });
      setActiveServiceAreaId(serviceAreaId);
      setSuccess('Active service area updated!');
    } catch (err: any) {
      console.error('Failed to set active service area:', err);
      setError('Failed to set active service area');
    }
  };

  const handleUpdateDROData = async () => {
    try {
      setLoading(true);
      setError('');
      await apiClient.post('/api/data/fetchDROData', {});
      setSuccess('DRO data updated successfully!');
      fetchServiceAreas();
    } catch (err: any) {
      console.error('Failed to fetch DRO data:', err);
      setError('Failed to update DRO data');
    } finally {
      setLoading(false);
    }
  };

  const handleProfilePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      setError('Please upload a JPG, PNG, or WEBP image');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be less than 5MB');
      return;
    }

    try {
      setUploadingPhoto(true);
      setError('');

      const formData = new FormData();
      formData.append('photo', file);

      const response = await apiClient.post<any, { photoUrl: string; user: User }>('/api/upload/profile-photo', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setUserInfo(response.user);
      // The topbar avatar reads the store, so the new photo has to land
      // there too or it does not appear until the next sign-in.
      updateUser(response.user);
      setSuccess('Profile photo updated successfully!');
    } catch (err: any) {
      console.error('Failed to upload photo:', err);
      setError(err.response?.data?.message || 'Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  // New user form handlers

  useEffect(() => {
    fetchServiceAreas();
    fetchUserInfo();
  }, [fetchServiceAreas, fetchUserInfo]);

  const anchor = useScrollSpy(SETTINGS_SECTIONS);

  // Arriving from another Settings page's panel (/settings#company): go to
  // that section once it has rendered.
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash) return undefined;
    const t = window.setTimeout(() => scrollToSection(hash.slice(1)), 60);
    return () => window.clearTimeout(t);
  }, [hash]);

  return (
    <>
      <SettingsPanel anchor={anchor} />

      <PageBody>
        <PageHeader
          title="Settings"
          subtitle="Your profile, and how this account is configured."
        />

        {/* The forms want a short measure; the column does not move. */}
        <Box sx={{ maxWidth: READING_WIDTH }}>
        {/* Alerts */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 3, whiteSpace: 'pre-wrap' }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        {/* Profile Info Section */}
        <Card id="profile" sx={{ scrollMarginTop: 24 }}>
          <CardContent>
            <Typography variant="h6" fontWeight="600" gutterBottom>
              Profile Information
            </Typography>

            {!userInfo ? (
              <LoadingState label="Loading your profile…" height={180} />
            ) : (
              <Box sx={{ mt: 3 }}>
                {/* Profile Photo */}
                <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 3 }}>
                  <Box
                    sx={{
                      width: 100,
                      height: 100,
                      borderRadius: '50%',
                      overflow: 'hidden',
                      bgcolor: 'grey.50',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '1px solid',
                    }}
                  >
                    {userInfo.profilePhoto ? (
                      <img
                        src={userInfo.profilePhoto}
                        alt="Profile"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      <Typography variant="h3" color="text.secondary">
                        {userInfo.firstName?.[0]}{userInfo.lastName?.[0]}
                      </Typography>
                    )}
                  </Box>
                  <Box>
                    <Button
                      variant="outlined"
                      component="label"
                      disabled={uploadingPhoto}
                     
                    >
                      {uploadingPhoto ? 'Uploading...' : 'Upload Photo'}
                      <input
                        type="file"
                        hidden
                        accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif"
                        onChange={handleProfilePhotoUpload}
                      />
                    </Button>
                    <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                      JPG, PNG or WEBP. Max 5MB.
                    </Typography>
                  </Box>
                </Box>

                <Divider sx={{ mb: 3 }} />

                {/* Basic Information */}
                <Box sx={{ mb: 4 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="subtitle1" fontWeight="600">
                      Basic Information
                    </Typography>
                    {!editingProfile && (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setEditingProfile(true)}
                       
                      >
                        Edit Profile
                      </Button>
                    )}
                  </Box>

                  {editingProfile ? (
                    <Box component="form" onSubmit={handleUserUpdate}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                          <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '250px' }}>
                            <TextField
                              label="First Name"
                              name="firstName"
                              value={userInfo.firstName || ''}
                              onChange={handleUserInfoChange}
                              fullWidth
                              required
                            />
                          </Box>
                          <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '250px' }}>
                            <TextField
                              label="Last Name"
                              name="lastName"
                              value={userInfo.lastName || ''}
                              onChange={handleUserInfoChange}
                              fullWidth
                              required
                            />
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                          <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '250px' }}>
                            <TextField
                              label="Email"
                              name="email"
                              value={userInfo.email || ''}
                              fullWidth
                              disabled
                              helperText="Email cannot be changed"
                            />
                          </Box>
                          <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '250px' }}>
                            <TextField
                              label="Phone Number"
                              name="phone"
                              value={userInfo.phone || ''}
                              onChange={handleUserInfoChange}
                              fullWidth
                              placeholder="+1 (555) 123-4567"
                            />
                          </Box>
                        </Box>
                      </Box>

                      {/* Action Buttons */}
                      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
                        <Button
                          variant="outlined"
                          onClick={() => {
                            setEditingProfile(false);
                            fetchUserInfo();
                          }}
                         
                        >
                          Cancel
                        </Button>
                        <Button
                          type="submit"
                          variant="contained"
                         
                        >
                          Save Changes
                        </Button>
                      </Box>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '250px' }}>
                          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                            First Name
                          </Typography>
                          <Typography variant="body1">
                            {userInfo.firstName || '-'}
                          </Typography>
                        </Box>
                        <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '250px' }}>
                          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                            Last Name
                          </Typography>
                          <Typography variant="body1">
                            {userInfo.lastName || '-'}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '250px' }}>
                          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                            Email
                          </Typography>
                          <Typography variant="body1">
                            {userInfo.email || '-'}
                          </Typography>
                        </Box>
                        <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '250px' }}>
                          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                            Phone Number
                          </Typography>
                          <Typography variant="body1">
                            {userInfo.phone || '-'}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  )}
                </Box>

                {/* DRO Credentials and Additional Info removed - these fields are in Account model, not User model */}
              </Box>
            )}
          </CardContent>
        </Card>

        {/* ------------------------------------------------- service areas */}
        {/* This card is new, but nothing in it is.
            `fetchServiceAreas`, `handleSetActive` and `handleUpdateDROData`
            were all written, all wired to real endpoints, and all
            unreachable — the screen fetched the list on mount and then
            never rendered it, so which service area you were operating on
            was set by the API and invisible in the product. */}
        <CompanyDetails />

        <Card id="service-areas" sx={{ mt: 3, scrollMarginTop: 24 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
              <Typography variant="h6">Service areas</Typography>
              <Button size="small" onClick={handleUpdateDROData} disabled={loading}>
                {loading ? 'Refreshing…' : 'Refresh from DRO'}
              </Button>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              The active area scopes schedules, workflows and the coverage map.
            </Typography>

            {loading && serviceAreas.length === 0 ? (
              <LoadingState label="Loading service areas…" height={140} />
            ) : serviceAreas.length === 0 ? (
              <EmptyState
                compact
                icon={<AreaIcon />}
                title="No service areas"
                description="Nothing has been synced from DRO for this company yet."
                action={{ label: 'Refresh from DRO', onClick: handleUpdateDROData }}
              />
            ) : (
              <Stack gap={1}>
                {serviceAreas.map((area) => {
                  const isActive = area.serviceAreaId === activeServiceAreaId;
                  return (
                    <Box
                      key={area.serviceAreaId}
                      sx={{
                        display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5,
                        border: '1px solid',
                        borderColor: isActive ? 'primary.main' : 'divider',
                        bgcolor: isActive ? 'primary.light' : 'transparent',
                        borderRadius: 2,
                      }}
                    >
                      <AreaIcon sx={{ fontSize: 19, color: isActive ? 'primary.dark' : 'text.disabled' }} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={600} noWrap>
                          {area.businessName || `Area ${area.serviceAreaId}`}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                          {[area.csa, area.stationName || area.stationId].filter(Boolean).join(' · ') || '—'}
                        </Typography>
                      </Box>
                      {isActive ? (
                        <ToneChip tone="ok" label="Active" />
                      ) : (
                        <Button size="small" onClick={() => handleSetActive(area.serviceAreaId)}>
                          Make active
                        </Button>
                      )}
                    </Box>
                  );
                })}
              </Stack>
            )}
          </CardContent>
        </Card>

        {/* One number, because one number is what there is. It exists so
            the outreach message can quote pay without the figure being
            typed onto every candidate first. */}
        <Card id="hiring" sx={{ mt: 3, scrollMarginTop: 24 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 0.5 }}>Hiring</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Defaults for new candidates. A rate agreed with one person is set in
              their own details and wins over this.
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={2}>
              {PAY_FIELDS.map(({ field, label, hint, step }) => (
                <TextField
                  key={field}
                  size="small"
                  type="number"
                  label={label}
                  value={pay[field]}
                  inputProps={step ? { step } : undefined}
                  onChange={(e) => {
                    setPay((p) => ({ ...p, [field]: e.target.value }));
                    setPaySaved(false);
                  }}
                  onBlur={(e) => savePay(field, e.target.value)}
                  helperText={hint}
                  sx={{ width: 200 }}
                />
              ))}
            </Stack>
            <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1 }}>
              {paySaved ? 'Saved.' : 'These are what the messages and the interview script quote.'}
            </Typography>

            <Divider sx={{ my: 3 }} />

            {/* The commonest ending in this pipeline is not a decision
                anybody makes: you text somebody and they never reply. */}
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Silence</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              A candidate the pipeline is waiting on — no reply to the first message, the
              licence you asked for on the call, a background check link they never
              opened — closes as &quot;Went quiet&quot; on its own. You are told by name and
              by reason the day before, and again the next time you open hiring. When the
              next move is yours, or a lab&apos;s, the clock does not run at all.
            </Typography>
            <TextField
              size="small"
              type="number"
              label="Close after"
              value={autoClose}
              onChange={(e) => { setAutoClose(e.target.value); setPaySaved(false); }}
              onBlur={(e) => savePay('autoCloseDays', e.target.value)}
              helperText={autoClose === '0' ? 'Off — nothing closes itself' : 'Days of silence. 0 turns it off.'}
              sx={{ width: 200 }}
            />

            <Divider sx={{ my: 3 }} />

            <MessageTemplates
              templates={templates}
              placeholders={placeholders}
              onChanged={setTemplates}
            />

            <Divider sx={{ my: 3 }} />

            {guide && (
              <InterviewGuideEditor
                guide={guide}
                placeholders={placeholders}
                onChanged={setGuide}
              />
            )}
          </CardContent>
        </Card>
        </Box>
      </PageBody>

    </>
  );
};

export default Settings;
